import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlay, FiDownload, FiChevronDown, FiChevronUp,
  FiRefreshCw, FiCheckCircle, FiAlertCircle, FiMinusCircle,
} from 'react-icons/fi';
import {
  runReconciliation, getReconciliations,
  getReconciliationDetail, exportReconciliation,
} from '../../utils/grirApi';

const MatchBadge = ({ status }) => {
  const cfg = {
    matched: { icon: <FiCheckCircle size={12} />, label: 'Matched', cls: 'match-matched' },
    partial: { icon: <FiAlertCircle size={12} />, label: 'Partial', cls: 'match-partial' },
    unmatched: { icon: <FiMinusCircle size={12} />, label: 'Unmatched', cls: 'match-unmatched' },
  };
  const { icon, label, cls } = cfg[status] || { icon: null, label: status, cls: '' };
  return <span className={`grir-badge ${cls}`}>{icon} {label}</span>;
};

const ReconciliationDetail = ({ id }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getReconciliationDetail(id)
      .then(setDetail)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <motion.div
      className="grir-detail-section"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      style={{ overflow: 'hidden' }}
    >
      {loading && (
        <div className="grir-loading-inline"><div className="grir-spinner-sm" /><span>Loading details…</span></div>
      )}
      {error && (
        <div className="grir-feedback error"><FiAlertCircle size={13} /> {error}</div>
      )}
      {detail && (
        <>
          <div className="grir-detail-stats">
            <span className="grir-detail-stat matched"><FiCheckCircle size={12} /> {detail.matched_records} Matched</span>
            <span className="grir-detail-stat partial"><FiAlertCircle size={12} /> {detail.partial_matches} Partial</span>
            <span className="grir-detail-stat unmatched"><FiMinusCircle size={12} /> {detail.unmatched_gr} Unmatched GR</span>
          </div>
          <div className="grir-table-wrap" style={{ paddingLeft: 0, paddingRight: 0 }}>
            <table className="grir-table">
              <thead>
                <tr>
                  <th>Status</th><th>GR ID</th><th>IR ID</th>
                  <th>Score</th><th>Method</th><th>Qty Var</th><th>Amt Var</th>
                </tr>
              </thead>
              <tbody>
                {detail.items?.map((item) => (
                  <tr key={item.id} className={`grir-row-${item.match_status}`}>
                    <td><MatchBadge status={item.match_status} /></td>
                    <td>{item.goods_receipt_id ?? '—'}</td>
                    <td>{item.invoice_receipt_id ?? '—'}</td>
                    <td>{item.match_score}%</td>
                    <td>{item.match_method}</td>
                    <td>{item.quantity_variance ?? '—'}</td>
                    <td>{item.amount_variance ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </motion.div>
  );
};

const STATUS_COLORS = {
  completed: '#22c55e',
  in_progress: '#0099b0',
  pending: '#f59e0b',
  failed: '#ef4444',
};

const formatDateTime = (str) =>
  str ? new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const rowVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 200, damping: 24 } },
};
const containerVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };

const GRIRReconciliation = () => {
  const [form, setForm] = useState({ name: '', description: '', start_date: '', end_date: '' });
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError] = useState('');
  const [reconciliations, setReconciliations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const loadList = useCallback(() => {
    setListLoading(true);
    getReconciliations({ limit: 50 })
      .then(setReconciliations)
      .catch(() => { })
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const handleRun = async () => {
    if (!form.name.trim()) { setRunError('Name is required.'); return; }
    setRunning(true); setRunResult(null); setRunError('');
    try {
      const data = await runReconciliation(form);
      setRunResult(data);
      setForm((f) => ({ ...f, name: '', description: '' }));
      loadList();
      setExpandedId(data.id);
    } catch (err) {
      setRunError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="grir-recon-page">
      {/* ── Run form ── */}
      <motion.div
        className="grir-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="grir-card-header">
          <div className="grir-card-title"><FiPlay size={16} /><span>New Reconciliation Run</span></div>
        </div>

        <div className="grir-form-grid">
          <div className="grir-form-group">
            <label>Name <span className="grir-required">*</span></label>
            <input className="grir-input" placeholder="e.g. March 2026 Reconciliation" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grir-form-group">
            <label>Description</label>
            <input className="grir-input" placeholder="Optional description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grir-form-group">
            <label>Start Date</label>
            <input className="grir-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div className="grir-form-group">
            <label>End Date</label>
            <input className="grir-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
        </div>

        <AnimatePresence>
          {runError && (
            <motion.div key="re" className="grir-feedback error" style={{ margin: '12px 20px 0' }}
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <FiAlertCircle size={14} /> {runError}
            </motion.div>
          )}
          {runResult && (
            <motion.div key="rr" className="grir-feedback success" style={{ margin: '12px 20px 0' }}
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <FiCheckCircle size={14} />
              <span>Run complete — <strong>{runResult.matched_records} matched</strong>, {runResult.partial_matches} partial, {runResult.unmatched_gr} unmatched GR.</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grir-card-footer">
          <motion.button className="grir-btn primary" onClick={handleRun} disabled={running} whileTap={{ scale: 0.96 }}>
            {running ? (<><div className="grir-btn-spinner" /> Running…</>) : (<><FiPlay size={14} /> Run Now</>)}
          </motion.button>
        </div>
      </motion.div>

      {/* ── Past runs ── */}
      <motion.div
        className="grir-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12, duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="grir-card-header">
          <div className="grir-card-title"><FiRefreshCw size={16} /><span>Past Reconciliation Runs</span></div>
          <motion.button className="grir-icon-btn" onClick={loadList} title="Refresh" whileTap={{ rotate: 180 }} transition={{ duration: 0.4 }}>
            <FiRefreshCw size={14} />
          </motion.button>
        </div>

        {listLoading ? (
          <div className="grir-loading-inline"><div className="grir-spinner-sm" /><span>Loading…</span></div>
        ) : reconciliations.length === 0 ? (
          <div className="grir-empty">No reconciliation runs yet. Use the form above to run your first reconciliation.</div>
        ) : (
          <motion.div className="grir-recon-list" variants={containerVariants} initial="hidden" animate="visible">
            {reconciliations.map((r) => {
              const color = STATUS_COLORS[r.status] || '#64748b';
              return (
                <motion.div key={r.id} className="grir-recon-row" variants={rowVariants}>
                  <div className="grir-recon-summary" onClick={() => toggleExpand(r.id)}>
                    <div className="grir-recon-name">
                      <span className="grir-recon-id">#{r.id}</span>
                      <span className="grir-recon-title">{r.name}</span>
                      <span className="grir-badge" style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}>{r.status}</span>
                    </div>
                    <div className="grir-recon-meta">
                      <span className="grir-recon-stat"><FiCheckCircle size={12} style={{ color: '#22c55e' }} />{r.matched_records} matched</span>
                      <span className="grir-recon-stat"><FiAlertCircle size={12} style={{ color: '#f59e0b' }} />{r.partial_matches} partial</span>
                      <span className="grir-recon-stat"><FiMinusCircle size={12} style={{ color: '#ef4444' }} />{r.unmatched_gr} unmatched</span>
                      <span className="grir-recon-date">{formatDateTime(r.completed_at)}</span>
                      <motion.button className="grir-icon-btn" onClick={(e) => { e.stopPropagation(); exportReconciliation(r.id); }} title="Export Excel" whileTap={{ scale: 0.88 }}>
                        <FiDownload size={13} />
                      </motion.button>
                      <motion.span animate={{ rotate: expandedId === r.id ? 180 : 0 }} transition={{ type: 'spring', stiffness: 260, damping: 22 }}>
                        <FiChevronDown size={16} style={{ color: '#64748b' }} />
                      </motion.span>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedId === r.id && <ReconciliationDetail id={r.id} />}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default GRIRReconciliation;
